import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { menuService } from '@/modules/menu/api/menuService';

/**
 * Menu Store - Bounded Context: modules/menu/
 * 
 * DECISIONES ARQUITECTÓNICAS (P1 Lite - 2025-12-20):
 * 
 * 1. ¿Por qué Zustand persist en lugar de React Query?
 *    - El menú se carga 1 vez al login (no necesita polling/invalidaciones)
 *    - Zustand persist ya hace caching automático
 *    - Con 2 devs frontend, simplicidad > perfección teórica
 *    - MIGRAR A REACT QUERY cuando:
 *      • Necesiten polling/real-time updates
 *      • Infinite scroll en menús
 *      • Equipo crezca a 3+ devs
 * 
 * 2. ¿Por qué está en modules/menu/store/?
 *    - Bounded Context correcto (menu es un módulo independiente)
 *    - Antes estaba en app/store/ (violación DDD)
 *    - Refactor P1 Lite movió aquí para mejor organización
 * 
 * 3. Consumers actuales:
 *    - modules/menu/model/useMenu.ts (su propio módulo) ✅
 *    - DashboardLayout (auto-ejecuta useMenu al montar)
 * 
 * @see P1 Lite ROI Evaluation para más contexto
 */


// Tipo para el menú transformado que usa el Sidebar
export interface TransformedMenuItem {
  id: number;
  nombre: string;
  descripcion?: string | null;
  url: string | null;
  icono?: string | null;
  orden: number;
  padre_id: number | null;
  activo: boolean;
  accesos: Array<{
    id: number;
    nombre: string;
    url: string | null;
  }>;
  children?: TransformedMenuItem[];
}

interface MenuState {
  menuItems: TransformedMenuItem[];
  isLoading: boolean;
  error: string | null;
  setMenuItems: (items: TransformedMenuItem[]) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearMenu: () => void;
  loadMenu: () => Promise<void>;
}

/**
 * Transforma recursivamente los items del menú del backend al formato del Sidebar
 */
const transformMenuItems = (items: any[]): TransformedMenuItem[] => {
  return items.map(item => ({
    id: item.id,
    nombre: item.nombre,
    descripcion: item.descripcion,
    url: item.url || null,
    icono: item.icono || null,
    orden: item.orden,
    padre_id: item.padreId || null,
    activo: item.activo,
    accesos: item.accesos || [],
    children: item.hijos && item.hijos.length > 0 ? transformMenuItems(item.hijos) : undefined,
  }));
};

export const useMenuStore = create<MenuState>()(
  persist(
    (set) => ({
      menuItems: [],
      isLoading: false,
      error: null,
      setMenuItems: (items) => {
        set({ menuItems: items, error: null });
      },
      setLoading: (loading) => {
        set({ isLoading: loading });
      },
      setError: (error) => {
        set({ error, isLoading: false });
      },
      clearMenu: () => {
        set({ menuItems: [], error: null, isLoading: false });
      },
      loadMenu: async () => {
        set({ isLoading: true, error: null });
        try {
          const menu = await menuService.getMenuUsuario();
          const transformedMenu = transformMenuItems(menu);
          set({ menuItems: transformedMenu, error: null, isLoading: false });
        } catch (error: any) {
          const errorMessage = error.message || 'Error al cargar el menú';
          set({ error: errorMessage, isLoading: false });
          console.error('Error al cargar el menú:', error);
        }
      },
    }),
    {
      name: 'menu-storage',
    }
  )
);
